import { format } from "@-/com.npmjs.date-fns";
import {
  getUserCardStage,
  getUserCardStageFromIntervalDays,
} from "@-/dev.improve.haniverse.lib.cards.user-card.mastery";
import type {
  CharacterStageReview,
  CharacterStageTimelinePoint,
  LearnedGraphemeRow,
  LearnedStudyCardRow,
  ProgressStageKey,
} from "@-/dev.improve.haniverse.lib.learning-stats.entities";

/**
 * Reconstruct end-of-day stages for the same distinct meaning cards counted by All.
 * FSRS logs are BEFORE-review snapshots: the next log supplies the result of the
 * preceding review, and the live FSRS card supplies the result of the last review.
 * Manual mastery takes effect at its saved timestamp, never retroactively.
 */
export function buildCharacterStageTimeline(
  cards: readonly LearnedStudyCardRow[],
  reviews: readonly CharacterStageReview[],
  now = new Date(),
): CharacterStageTimelinePoint[] {
  return buildStageTimeline(cards, reviews, now, new Map(cards.map((card) => [card.id, 1]))).map(
    ({ dayKey, valuesByStage }) => ({
      charactersByStage: valuesByStage,
      dayKey,
      learnedCount: Object.values(valuesByStage).reduce((sum, value) => sum + value, 0),
    }),
  );
}

/** Attribute corpus frequency to each character's stage at the selected date. */
export function buildCoverageStageTimeline(
  cards: readonly LearnedGraphemeRow[],
  reviews: readonly CharacterStageReview[],
  now = new Date(),
) {
  const weights = new Map(
    cards.map((card) => [card.id, Math.max(0, card.frequencyPerMillion ?? 0)]),
  );
  return buildStageTimeline(cards, reviews, now, weights).map(({ dayKey, valuesByStage }) => {
    const totalFrequency = Object.values(valuesByStage).reduce((sum, value) => sum + value, 0);
    // Normalize per-million frequencies to percentages; preserve relative contributions if
    // corpus rounding takes the full learned set slightly above one million.
    const divisor = Math.max(1_000_000, totalFrequency) / 100;
    return {
      coveragePercentByStage: {
        familiar: valuesByStage.familiar / divisor,
        mastered: valuesByStage.mastered / divisor,
        new: valuesByStage.new / divisor,
        proficient: valuesByStage.proficient / divisor,
      },
      dayKey,
    };
  });
}

function buildStageTimeline(
  cards: readonly LearnedStudyCardRow[],
  reviews: readonly CharacterStageReview[],
  now: Date,
  weights: ReadonlyMap<string, number>,
): { dayKey: string; valuesByStage: Record<ProgressStageKey, number> }[] {
  const reviewsByCard = new Map<string, CharacterStageReview[]>();
  for (const review of reviews) {
    const bucket = reviewsByCard.get(review.cardId) ?? [];
    bucket.push(review);
    reviewsByCard.set(review.cardId, bucket);
  }

  type StageEvent = { cardId: string; stage: ProgressStageKey; time: number };
  const events: StageEvent[] = [];
  for (const card of cards) {
    const started = new Date(card.startedStudyingAt).getTime();
    if (!Number.isFinite(started) || started > now.getTime()) {
      continue;
    }
    const mastered =
      card.manuallyMasteredAt === null ? null : new Date(card.manuallyMasteredAt).getTime();
    const cardReviews = [...(reviewsByCard.get(card.id) ?? [])].sort(
      (a, b) => new Date(a.reviewedAt).getTime() - new Date(b.reviewedAt).getTime(),
    );
    const naturalStage = getUserCardStage({
      fsrsCard: { lastReview: card.lastReview, scheduledDays: card.scheduledDays },
      manuallyMasteredAt: null,
    });
    const addEvent = (time: number, stage: ProgressStageKey) => {
      if (Number.isFinite(time) && time <= now.getTime()) {
        events.push({
          cardId: card.id,
          stage: mastered !== null && time >= mastered ? "mastered" : stage,
          time: Math.max(started, time),
        });
      }
    };
    addEvent(started, "new");
    for (const [index, review] of cardReviews.entries()) {
      const nextReview = cardReviews[index + 1];
      addEvent(
        new Date(review.reviewedAt).getTime(),
        nextReview === undefined
          ? naturalStage
          : getUserCardStageFromIntervalDays(nextReview.scheduledDays),
      );
    }
    // Imported cards may have a last-review snapshot without older review logs.
    if (card.lastReview !== null) {
      addEvent(new Date(card.lastReview).getTime(), naturalStage);
    }
    if (mastered !== null) {
      addEvent(mastered, "mastered");
    }
    // Reconcile today's endpoint with the current card after edits/imports.
    addEvent(now.getTime(), card.stage);
  }

  events.sort((a, b) => a.time - b.time);
  const stagesByCard = new Map<string, ProgressStageKey>();
  const counts: Record<ProgressStageKey, number> = {
    familiar: 0,
    mastered: 0,
    new: 0,
    proficient: 0,
  };
  const days = new Map<
    string,
    { dayKey: string; valuesByStage: Record<ProgressStageKey, number> }
  >();
  for (const event of events) {
    const previous = stagesByCard.get(event.cardId);
    if (previous === event.stage) {
      continue;
    }
    if (previous !== undefined) {
      counts[previous] = Math.max(0, counts[previous] - (weights.get(event.cardId) ?? 0));
    }
    counts[event.stage] += weights.get(event.cardId) ?? 0;
    stagesByCard.set(event.cardId, event.stage);
    const dayKey = format(new Date(event.time), "yyyy-MM-dd");
    days.set(dayKey, { dayKey, valuesByStage: { ...counts } });
  }
  return [...days.values()];
}
